/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo, useEffect, useState } from "react";
import { Chip, Typography, CircularProgress, Box, SxProps, Button, IconButton } from "@mui/material";
import { Badge } from "../../components/ui/badge";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import ConfigGroupList from "../Configuration/ConfigGroupList";
import EventDisplay from "../Nodes/EventDisplay/EventDisplay";
import AlarmDisplay from "../Nodes/AlarmDisplay/AlarmDisplay";
import ConditionDisplay from "../Nodes/ConditionDisplay/ConditionDisplay";
import "./DeviceDetails.css";
import { useSelector } from "react-redux";
import { deviceDataCountSelector, noOfSelectedRowSelector, selectedDeviceSelector } from "../../store/api/devices/selectors";
import { IFullDeviceDetails } from "../../store/api/deviceConfig/types";
import { useStoreDispatch } from "../../store/hooks";
import { resetDeviceFilters } from "../../store/filters/Nodes/reducer";
import { resetKeyword } from "../../store/search/reducer";
import { useSummaryDisplay } from "../Configuration/Summary/hooks";
import { useDeviceDrawerDetails } from "./hooks";
import {
  alarmsCountSelector,
  confirmDialogSelector,
  isLoadingStatusSelector,
  serverMaintenanceModeSelector,
} from "../../store/helpers/helperSelectors";
import DataLoader from "../../components/Shared/Loader/DataLoader";
import { resetPaginationModel } from "../../store/helpers/helperReducers";
import { isDasModel, prepareAlarmCountData, prepareDeviceDataCountForSummary } from "../Configuration/helpers";
import { getBaseDomain } from "../../utils/cookie";
import { deviceDrawerOptions } from "../../components/Actions/ActionOptions";
import { prepareActionProps } from "../../components/Actions/ActionHelper";
import Actions from "../../components/Actions/Actions";
import TemplatesOnDeviceDetailTab from "../../components/CommisioningGrid/TemplatesOnDeviceDetailTab";
import TemplateLogs from "../Tasks/TemplateLogs";
import ConfirmDialog from "../../components/Shared/Dialogs/ConfirmDialog";
import { useTaskTemplateDisplay } from "../Tasks/hooks";
import { API, DATEFORMATS, DIALOGOPENFOR, LOCAL_STORAGE } from "../../globals/globals";
import { setIsDeviceDetailPage, resetEventFilters } from "../../store/filters/events/EventFilterReducer";
import { constStr } from "../../globals/constants";
import { isTemplateSelector } from "../../store/api/tasks/selector";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import { setConfigCounts } from "../../store/api/deviceConfig/reducers";
import { IdeviceCounts } from "../../store/api/devices/types";
import { setNoOfSelectedRow, setSelectedType } from "../../store/api/devices/reducers";
import { useGetDeviceByKeyQuery } from "../../store/api/deviceConfig/deviceConfigApiSlice";
import { selectedGeoRegionsSelector } from "../../store/api/admin/selectors";
import { useGetDasRadioNodesQuery, useGetDasFeatureFlagsQuery } from "../../store/api/devices/deviceApiSlice";
import { resolveDeviceStatusLabel } from "../../utils/deviceStatus";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Notes from "./Notes/Notes";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { capitalizeWords } from "../../utils/helpers";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Icon } from "../../components/Shared/Icon";
import { IGeoRegion } from "../../store/api/admin/types";
import { getDeviceStatusIndicatorConfig } from "../../components/helpers";

dayjs.extend(utc);

const DeviceDetails: React.FC = () => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const deviceType = location?.pathname?.split("/")[3];
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedTemplate = useSelector(isTemplateSelector);
  const [isActionTrue, setIsActionTrue] = useState(false);
  const { notesCount } = useDeviceDrawerDetails();
  useSummaryDisplay();

  const [activeTab, setActiveTab] = useState("alarms");
  const tabsSectionRef = React.useRef<HTMLDivElement>(null);

  const isPerformance = location.pathname.endsWith("/performance");
  const isTopologyView = location.pathname.includes("/das-topology");

  // Scroll to tabs section when navigated with scrollToTab state
  useEffect(() => {
    const state = location.state as { scrollToTab?: string } | undefined;
    if (state?.scrollToTab) {
      setActiveTab(state.scrollToTab);
      // Small delay to allow render, then scroll
      const timer = setTimeout(() => {
        tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      // Clear the state so it doesn't re-scroll on re-renders
      window.history.replaceState({}, '');
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const { handleDeleteTemplate } = useTaskTemplateDisplay();
  const alarmsCount = useSelector(alarmsCountSelector);
  const serverMaintenanceMode = useSelector(serverMaintenanceModeSelector);
  const loadingStatus = useSelector(isLoadingStatusSelector);
  const selectedRows = useSelector(noOfSelectedRowSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const selectedGeoRegions = useSelector(selectedGeoRegionsSelector) as IGeoRegion[];
  const locationPath = location?.pathname.split("/");
  const deviceObjectKey = selectedDevice?.objectKey || locationPath[2];

  // Reset all grid pagination to page 0 when navigating to a different device
  useEffect(() => {
    dispatch(resetPaginationModel());
  }, [deviceObjectKey, dispatch]);


  const args = {
    filters: `key=objectKey&value=${deviceObjectKey}&weightType=FULL`,
    deviceKey: deviceObjectKey,
  };

  const { data } = useGetDeviceByKeyQuery(args, {
    pollingInterval: API.ENDPOINTS.DEVICEBYKEY.pollingInterval || API.POLLING_INTERVAL,
    skip: !!deviceObjectKey ? false : true,
  });

  const { configurationStatus } = selectedDevice;
  const isOfflineDevice = configurationStatus?.toLowerCase() === "offline";

  useEffect(() => {
    if (Object.keys(selectedRows)?.length > 0) {
      for (const key in selectedRows) {
        if (selectedRows[key]?.length > 0) {
          setIsActionTrue(true);
        } else {
          isActionTrue && setIsActionTrue(false);
        }
      }
    }
  }, [isActionTrue, selectedRows]);

  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const isDas = isDasModel(selectedDevice.modelName);
  const nodeType = isDas ? selectedDevice.modelName : deviceDetail.nodeType;

  const { data: dasFeatureFlags } = useGetDasFeatureFlagsQuery(selectedDevice.modelName, {
    skip: !isDas || !selectedDevice.modelName,
  });
  const inventoryDiscoveryEnabled = isDas ? (dasFeatureFlags?.inventoryDiscovery ?? true) : true;

  const { data: dasRadioNodes = [] } = useGetDasRadioNodesQuery(
    { deviceKey: deviceObjectKey },
    {
      skip: !isDas || !deviceObjectKey || !inventoryDiscoveryEnabled,
      pollingInterval: 30000,
      refetchOnMountOrArgChange: 30,
    }
  );

  const dasRadioNodeCounts = useMemo(() => {
    const down = dasRadioNodes.reduce((count, item) => {
      return resolveDeviceStatusLabel(item?.status) === "Disconnected" ? count + 1 : count;
    }, 0);
    const up = Math.max(dasRadioNodes.length - down, 0);
    return { up, down };
  }, [dasRadioNodes]);
  // Ensure filters know we're on a device details page (and reset on unmount)
  useEffect(() => {
    dispatch(setIsDeviceDetailPage(true));
    dispatch(setSelectedType("alarms"));
    return () => {
      dispatch(setIsDeviceDetailPage(false));
    };
  }, [dispatch]);

  const tabData = [
    {
      label: "Alarms",
      value: "alarms",
      icon: <Badge variant="secondary" className="shrink-0 h-5 min-w-[20px] px-1.5 text-xs tabular-nums justify-center">{alarmsCount}</Badge>,
      iconPosition: "end",
      component: <AlarmDisplay key={deviceObjectKey} />,
    },
    {
      label: "Events",
      value: "events",
      component: <EventDisplay />,
    },
    {
      icon: <Badge variant="secondary" className="shrink-0 h-5 min-w-[20px] px-1.5 text-xs tabular-nums justify-center">{notesCount > 9 ? "9+" : notesCount}</Badge>,
      label: "Notes",
      value: "notes",
      component: <Notes />,
    },
  ];

  if (!isDasModel(selectedDevice.modelName)) {
    tabData.push({ label: "Conditions", value: "conditions", component: <ConditionDisplay /> });
  } 

  const commissioningTabData = [
    {
      label: "Template Log",
      value: "template-log",
      component: <TemplateLogs />,
    },
    {
      label: "Local Templates",
      value: "local-templates",
      component: <TemplatesOnDeviceDetailTab templateType="Local" />,
    },
    {
      label: "Alarms",
      value: "alarms",
      icon: <Badge variant="secondary" className="shrink-0 h-5 min-w-[20px] px-1.5 text-xs tabular-nums justify-center">{alarmsCount}</Badge>,
      iconPosition: "end",
      component: <AlarmDisplay key={deviceObjectKey} />,
    },
    {
      label: "Events",
      value: "events",
      component: <EventDisplay />,
    },
    {
      label: "Conditions",
      value: "conditions",
      component: <ConditionDisplay />,
    },
    {
      icon: <Badge variant="secondary" className="shrink-0 h-5 min-w-[20px] px-1.5 text-xs tabular-nums justify-center">{1}</Badge>,
      label: "Notes",
      value: "notes",
      component: <Notes />,
    },
  ];

  const breadcrumbHandler = () => {
    dispatch(resetDeviceFilters());
    dispatch(resetKeyword());
    dispatch(setConfigCounts({}));
  };

  useEffect(() => {
    const deviceRegionName = deviceDetail?.geoRegionName?.trim();

    if (!deviceRegionName || selectedGeoRegions.length === 0) {
      return;
    }

    const deviceRegionIsSelected = selectedGeoRegions.some(
      (region) => region?.name?.trim() === deviceRegionName
    );

    if (!deviceRegionIsSelected) {
      breadcrumbHandler();
      navigate("/nodes", { replace: true });
    }
  }, [deviceDetail?.geoRegionName, navigate, selectedGeoRegions]);

  useEffect(() => {
    if (deviceType) {
      dispatch(setNoOfSelectedRow({ deviceRowIds: [] }));
      const firstTab = deviceType === "commissioning" ? "template-log" : "alarms";
      setActiveTab(firstTab);
      dispatch(setSelectedType(deviceType === "commissioning" ? "none" : "alarms"));
    }
  }, [deviceType, dispatch]);

  const handleDeleteTemplateAction = (actionName: string) => {
    if (!actionName) return;
    let args = { template: selectedTemplate || "" };
    if (args?.template) {
      handleDeleteTemplate(args, actionName);
    }
  };

  const ActionBtnProp = true;

  const deviceDataCount: IdeviceCounts = useSelector(deviceDataCountSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const actionProps = useMemo(
    () =>
      prepareActionProps(
        selectedDevice?.objectKey!,
        selectedDevice?.name || "",
        selectedDevice,
        deviceDrawerOptions,
        "deviceDrawer",
        ActionBtnProp,
        permissions,
        inventoryDiscoveryEnabled
      ),
    [ActionBtnProp, permissions, selectedDevice, inventoryDiscoveryEnabled]
  );


  const ipAddress = deviceDetail?.ipAddress;
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (ipAddress == null || permissions == null || ipAddress.length === 0) {
      return;
    }
    if (permissions.hasOpCodeGroup("manage_sn")) {
      setAllowed(true);
    }
  }, [ipAddress, permissions]);

  const WebTerminalClicked = () => {
    if (allowed && ipAddress && isDasModel(deviceDetail.modelName)) {
      const baseDomain = getBaseDomain();
      // Route the DAS GUI through the auth-gated proxy-<deviceId> subdomain so
      // the device IP/port is never exposed. Fall back to a direct link when
      // there is no usable base domain (e.g. IP/localhost dev hosts).
      if (baseDomain && deviceObjectKey) {
        window.open(`${window.location.protocol}//proxy-${deviceObjectKey}.${baseDomain}/`, "_blank");
      } else {
        window.open("https://" + ipAddress.replace(/^\[|\]$/g, ""), "_blank");
      }
    }
  }
  const alarmCount: any = prepareAlarmCountData(selectedDevice);

  const rowHeader: any = prepareDeviceDataCountForSummary(deviceDataCount, selectedDevice?.nodeType);
  const marginTop =
    serverMaintenanceMode && (selectedDevice?.status === 19 || selectedDevice?.operationMode === "Maintenance")
      ? 10
      : serverMaintenanceMode
        ? 6
        : selectedDevice?.status === 19 || selectedDevice?.operationMode === "Maintenance"
          ? 3
          : "";

  const ueCurrentSession = selectedDevice.nodeType === "SN" ? selectedDevice?.lteCurrentUESession : selectedDevice?.ngCurrentUESession || "";
  const ueMaxSession = selectedDevice.nodeType === "SN" ? selectedDevice?.lteMaxUESession : selectedDevice?.ngMaxUESession || "";
  const ueSession = ueCurrentSession && ueMaxSession ? `${ueCurrentSession} (${ueMaxSession} max)` : "";

  // Ensure filters know we're on a device details page (and reset on unmount)
  useEffect(() => {
    dispatch(setIsDeviceDetailPage(true));
    return () => {
      dispatch(setIsDeviceDetailPage(false));
    };
  }, [dispatch]);

  function DeviceStatusIndicator({
    isOffline,
    statusCode,
    operationMode,
  }: {
    isOffline: boolean;
    statusCode?: number;
    operationMode?: string;
  }) {
    const statusIndicatorConfig = getDeviceStatusIndicatorConfig(
      { isOffline, statusCode, operationMode },
      {
        offlineTextClassName: "text-sidebar-foreground",
        connectedTextClassName: "text-sidebar-foreground",
      }
    );

    return (
      <span className={statusIndicatorConfig.wrapperClassName}>
        <Icon name={statusIndicatorConfig.iconName} size={14} className={statusIndicatorConfig.iconClassName} />
        <span className="truncate">{statusIndicatorConfig.label}</span>
      </span>
    );
  }

  return (
    <>
      {!!deviceDetail ? (
        <div className="flex flex-col" style={{ minHeight: "calc(100vh - 48px)" }}>
          {openConfirmDialog === DIALOGOPENFOR.LOCALPANELEMPLATE && (
            <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteTemplateAction}>
              Are you sure you want to delete <strong>{`${selectedTemplate}` || "Unknown"}</strong>?
            </ConfirmDialog>
          )}

          {/* ── Top bar header ── */}
          <div className="shrink-0 overflow-hidden rounded-b-lg dark:border-b dark:border-white/10" style={{ backgroundColor: "#0F172A" }}>
            {/* Status strip */}
            {isOfflineDevice ? null : selectedDevice?.status === 19 ? (
              <div className="h-1 bg-destructive" />
            ) : selectedDevice?.operationMode === "Maintenance" ? (
              <div className="h-1 bg-amber-500" />
            ) : null}

            <div className="px-4 py-6 md:px-6 lg:px-8">
              <div className="flex items-start justify-between gap-8 flex-nowrap">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <Link
                    to="/nodes"
                    onClick={breadcrumbHandler}
                    className="shrink-0 -ml-2 mt-1.5 inline-flex items-center justify-center h-8 w-8 rounded-md text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                  >
                    <span className="material-icons" style={{ fontSize: 24 }}>chevron_left</span>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <h1
                      className="text-3xl font-semibold tracking-tight text-sidebar-foreground mb-2 leading-tight mt-0 truncate"
                      title={deviceDetail?.name || ""}
                    >
                      {deviceDetail?.name?.toUpperCase() || "Unknown"}
                    </h1>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="bg-gray-700 text-gray-200 hover:bg-gray-600 border-0">
                        {deviceDetail?.nodeType}
                      </Badge>
                      <Badge variant="secondary" className="font-normal border-0" style={{ backgroundColor: "#172c65", color: "#F8FAFC" }}>{deviceDetail?.geoRegionName}</Badge>
                      <DeviceStatusIndicator
                        isOffline={!!isOfflineDevice}
                        statusCode={selectedDevice?.status}
                        operationMode={selectedDevice?.operationMode}
                      />
                    </div>
                  </div>
                </div>

                {/* Right: Stats + last updated + alarms + actions */}
                <div className="flex items-center gap-10 shrink-0 whitespace-nowrap">
                  {/* Row headers (IP interfaces, Radio nodes, etc.) */}
                  {!isDas && (
                  <div className="flex items-center gap-10 text-sm">
                    {rowHeader.length > 0 &&
                      rowHeader.map((item: any) => (
                        <div key={item.key}>
                          <p className="text-gray-400 mb-0.5 text-sm">{item?.key}</p>
                          <div className="flex items-center gap-1.5 text-sidebar-foreground">
                            {loadingStatus["deviceCounts"] ? (
                              <span className="text-sidebar-accent-foreground text-xs">—</span>
                            ) : (
                              <>
                                <span className="material-icons text-success" style={{ fontSize: 14 }}>arrow_upward</span>
                                <span className="font-semibold tabular-nums">{item?.upValue}</span>
                                <span className="material-icons text-destructive ml-1" style={{ fontSize: 14 }}>arrow_downward</span>
                                <span className="font-semibold tabular-nums">{item?.downValue}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    {/* UE Sessions */}
                    <div>
                      <p className="text-gray-400 mb-0.5 text-sm">UE Sessions</p>
                      <span className="font-semibold tabular-nums text-sidebar-foreground text-sm">
                        {loadingStatus["deviceCounts"] ? "—" : ueSession || "—"}
                      </span>
                    </div>
                  </div>
                  )}

                  {isDas && (
                    <div className="flex items-center gap-10 text-sm">
                      <div>
                        <p className="text-gray-400 mb-0.5 text-sm">Radio Nodes</p>
                        <div className="flex items-center gap-1.5 text-sidebar-foreground">
                          {loadingStatus["deviceCounts"] ? (
                            <span className="text-sidebar-accent-foreground text-xs">—</span>
                          ) : (
                            <>
                              <span className="material-icons text-success" style={{ fontSize: 14 }}>arrow_upward</span>
                              <span className="font-semibold tabular-nums">{dasRadioNodeCounts.up}</span>
                              <span className="material-icons text-destructive ml-1" style={{ fontSize: 14 }}>arrow_downward</span>
                              <span className="font-semibold tabular-nums">{dasRadioNodeCounts.down}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Last Updated Time */}
                  <div className="text-sm">
                    <p className="text-gray-400 mb-0.5 text-sm text-center">Last Updated</p>
                    <Badge
                      className={`border-0 text-white ${selectedDevice.status === 19
                        ? isOfflineDevice ? "bg-gray-700" : "bg-destructive"
                        : "bg-success"
                        }`}
                    >
                      {loadingStatus["deviceCounts"]
                        ? "—"
                        : (data?.data && dayjs(data.data[0].lastInform).utc().format(DATEFORMATS.TS) + " UTC") || ""}
                    </Badge>
                  </div>

                  {/* Alarm counts */}
                  <div className="flex items-center gap-10 text-sm">
                    {alarmCount?.length > 0 &&
                      alarmCount.map((item: any) => (
                        <div key={item.key}>
                          <p className="text-gray-400 mb-0.5 text-sm">{capitalizeWords(item?.key)}</p>
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`material-icons ${item.key === "CRITICAL" ? "text-destructive" : "text-amber-500"}`}
                              style={{ fontSize: 14 }}
                            >
                              {item.key === "CRITICAL" ? "error" : "error_outline"}
                            </span>
                            <span className="font-semibold tabular-nums text-sidebar-foreground">{item?.downValue}</span>
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Actions */}
                  <div>{!!actionProps && <Actions actionProps={actionProps} disabled={isActionTrue} variant="dark" />}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Content area with sidebar ── */}
          <div className="flex gap-8 flex-1 px-4 py-6 md:px-6 lg:px-8 bg-background">
            {/* Left sidebar */}
            <div
              className="w-48 shrink-0 sticky top-4 self-start rounded-xl border bg-card text-card-foreground shadow p-2 overflow-hidden"
              style={{ maxHeight: "calc(100vh - 240px)", overflow: "auto" }}
            >
              <ConfigGroupList deviceDetail={deviceDetail} />
            </div>

            {/* Main content */}
            <div
              className="flex-1 min-w-0 rounded-md bg-background"
            >
              <div style={{ minHeight: 285 }} className="mb-6">
                <Outlet />
              </div>

              {!isPerformance && !isTopologyView && (
                <div
                  ref={tabsSectionRef} style={{
                    minHeight: 285,
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius)",
                    padding: "18px",
                    boxShadow: "var(--shadow-card)",
                    backgroundColor: "var(--card)",
                  }}
                  className="pt-2"
                >
                  <Tabs
                    value={activeTab}
                    onValueChange={(val) => {
                      setActiveTab(val);
                      if (val.includes("alarms")) {
                        dispatch(setSelectedType("alarms"));
                      } else if (val === "local-templates") {
                        dispatch(setSelectedType("localTab"));
                      } else {
                        dispatch(setSelectedType("none"));
                      }
                      dispatch(resetKeyword());
                      dispatch(resetEventFilters());
                    }}
                  >
                    <div className="pl-1 pr-1 mt-4">
                      <div style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
                          {!isPerformance && !isTopologyView && ((deviceType === "commissioning" ? commissioningTabData : tabData)).map((tab) => (
                            <TabsTrigger
                              key={tab.value}
                              value={tab.value}
                              className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            >
                              <span className="flex items-center gap-1.5">
                                {tab.label}
                                {tab.icon && tab.icon}
                              </span>
                            </TabsTrigger>
                          ))}
                        </TabsList>
                      </div>
                    </div>
                    {!isPerformance && !isTopologyView && ((deviceType === "commissioning" ? commissioningTabData : tabData).map((tab) => (
                      <TabsContent key={tab.value} value={tab.value} className="pt-1">
                        {tab.component}
                      </TabsContent>
                    )))}
                  </Tabs>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center" style={{ height: 500 }}>
          <DataLoader />
        </div>
      )}
    </>
  );
};

export default DeviceDetails;
